import { useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ListShell } from "@/components/shared/ListShell";
import { PermissionGate } from "@/components/rbac/PermissionGate";
import { ListToolbar } from "@/components/shared/ListToolbar";
import { ListPagination } from "@/components/shared/ListPagination";
import {
  DataTable,
  type DataTableColumn,
  type SelectionMode,
} from "@/components/shared/DataTable";
import { SelectionModeControl } from "@/components/shared/SelectionModeControl";
import {
  useReviewList,
  useModerateReview,
  useDeleteReview,
  type ReviewRow,
} from "@/hooks/useReviews";
import { ReviewStatus } from "@/hooks/useReviews";
import { formatDate } from "@/utils/format";
import { downloadCsv } from "@/utils/exportCsv";
import { useAuth } from "@/contexts/AuthContext";

type TabKey = "all" | ReviewStatus;

const tabs: Array<{ key: TabKey; label: string }> = [
  { key: "all", label: "All" },
  { key: ReviewStatus.PUBLISHED, label: "Published" },
  { key: ReviewStatus.FLAGGED, label: "Flagged" },
  { key: ReviewStatus.REMOVED, label: "Removed" },
];

const DEFAULT_PAGE_SIZE = 25;

const exportRows = (rows: ReviewRow[]) =>
  downloadCsv(
    "reviews",
    ["Rating", "Centre", "Status", "Review", "Created"],
    rows.map((r) => [
      String(r.ratingOverall),
      r.centre?.name ?? "",
      String(r.status),
      r.reviewText ?? "",
      formatDate(r.createdAt),
    ]),
  );

export const ReviewsList = () => {
  const [tab, setTab] = useState<TabKey>("all");
  const [selection, setSelection] = useState<SelectionMode>("none");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const { can } = useAuth();

  const { data, isLoading, error, refetch } = useReviewList({
    status: tab === "all" ? undefined : (tab as ReviewStatus),
    page,
    limit,
  });
  const moderate = useModerateReview();
  const remove = useDeleteReview();
  const rows = data?.data ?? [];
  const pagination = data?.pagination;

  const setSelectionMode = (m: SelectionMode) => {
    setSelection(m);
    setSelectedIds([]);
  };

  const handleModerate = async (id: string, status: ReviewStatus) => {
    try {
      await moderate.mutateAsync({ id, status });
      toast.success("Review updated");
      refetch();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete review permanently?")) return;
    try {
      await remove.mutateAsync(id);
      toast.success("Deleted");
      refetch();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const columns: Array<DataTableColumn<ReviewRow>> = [
    {
      key: "rating",
      header: "Rating",
      render: (r) => (
        <span className="inline-flex items-center gap-1 text-sm font-semibold">
          <Star className="w-4 h-4 text-warning fill-warning" />
          {r.ratingOverall}
        </span>
      ),
    },
    { key: "centre", header: "Centre", render: (r) => r.centre?.name ?? "—" },
    {
      key: "review",
      header: "Review",
      render: (r) => (
        <div className="max-w-md space-y-1">
          {r.reviewText ? (
            <span className="text-muted-foreground line-clamp-1 block">
              {r.reviewText}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
          {r.providerReply && (
            <span className="text-xs text-primary/70 line-clamp-1 block pl-2 border-l-2 border-primary/30">
              Provider: {r.providerReply}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (r) => <StatusBadge status={r.status} />,
    },
    {
      key: "created",
      header: "Created",
      render: (r) => (
        <span className="text-muted-foreground">{formatDate(r.createdAt)}</span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (r) => (
        <div
          className="flex gap-2 justify-end"
          onClick={(e) => e.stopPropagation()}
        >
          {r.status !== ReviewStatus.PUBLISHED && (
            <PermissionGate module="reviews" action="moderate">
              <button
                type="button"
                onClick={() => handleModerate(r.id, ReviewStatus.PUBLISHED)}
                className="px-2.5 py-1 text-xs rounded-btn bg-success text-success-foreground hover:opacity-90"
              >
                Publish
              </button>
            </PermissionGate>
          )}
          {r.status !== ReviewStatus.REMOVED && (
            <PermissionGate module="reviews" action="moderate">
              <button
                type="button"
                onClick={() => handleModerate(r.id, ReviewStatus.REMOVED)}
                className="px-2.5 py-1 text-xs rounded-btn bg-muted text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              >
                Remove
              </button>
            </PermissionGate>
          )}
          <PermissionGate module="reviews" action="delete">
            <button
              type="button"
              onClick={() => handleDelete(r.id)}
              className="px-2.5 py-1 text-xs rounded-btn bg-destructive text-destructive-foreground hover:opacity-90"
            >
              Delete
            </button>
          </PermissionGate>
        </div>
      ),
    },
  ];

  return (
    <AppLayout title="Reviews">
      <ListToolbar<TabKey, string>
        tabs={{
          value: tab,
          onChange: (v) => {
            setTab(v);
            setPage(1);
          },
          options: tabs.map((t) => ({ key: t.key, label: t.label })),
        }}
        onExport={can("reviews", "export") ? () => exportRows(rows) : undefined}
        actions={
          <SelectionModeControl value={selection} onChange={setSelectionMode} />
        }
      />

      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-btn px-4 py-2 mb-3 text-sm">
          <span className="font-medium">{selectedIds.length} selected</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                toast.success(`Published ${selectedIds.length} reviews`);
                setSelectedIds([]);
              }}
              className="px-3 py-1 rounded-btn border border-border hover:bg-muted"
            >
              Publish
            </button>
            <button
              type="button"
              onClick={() => {
                toast.error(`Removed ${selectedIds.length} reviews`);
                setSelectedIds([]);
              }}
              className="px-3 py-1 rounded-btn border border-border hover:bg-muted"
            >
              Remove
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              className="px-3 py-1 rounded-btn text-muted-foreground hover:bg-muted"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <ListShell
        isLoading={isLoading}
        error={error}
        isEmpty={rows.length === 0}
        onRetry={refetch}
      >
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          selection={selection}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
        <ListPagination
          pagination={pagination}
          page={page}
          rowsShown={rows.length}
          itemLabel="reviews"
          onPageChange={setPage}
          limit={limit}
          onLimitChange={(n) => {
            setLimit(n);
            setPage(1);
          }}
        />
      </ListShell>
    </AppLayout>
  );
};

export default ReviewsList;
